// Door Ranges — the shared authoring page.
//
// Pixel-parity port of stage's supplier-portal-tkc.html (lines 9415–10442).
// Self-publish workflow: "Save & Publish" sets status:"live" directly, no
// admin approval queue. Doors-only category for now (CATEGORIES schema is
// extensible for future product types).
//
// Mounted by BOTH the supplier portal (scope "self") and the admin portal
// (scope "admin", with a supplier picker). See ./types.ts for why that is the
// only difference between the two.
//
// Backend: kitchenos-api/modules/door_ranges.js, mounted at /api/door-ranges:
//   GET    /api/door-ranges                  list — supplier-scoped by cookie;
//                                            admin passes ?supplierId=<slug>
//   GET    /api/door-ranges/:id              fetch one
//   POST   /api/door-ranges                  create (status as supplied)
//   PUT    /api/door-ranges/:id              update
//   DELETE /api/door-ranges/:id              delete
//   POST   /api/door-ranges/upload-image     multipart upload → { urls: [...] }
//
// Auth (2026-08-17): every write requires a supplier or admin cookie, and a
// supplier can only touch its own ranges. A supplier NEVER sends supplierId —
// the server takes identity from the cookie, so the body cannot spoof it. Only
// admin sends supplierId, and the server validates it against the supplier
// directory.
//
// We deliberately keep:
//   - frontalImage field per colour (modular schema has it; useful for QB)
//   - "View" read-only mode for live ranges (jump to Edit if you want to change)

import type {
  BuilderContext,
  BuilderCleanup,
  BuilderPage,
  BuilderOptions,
  BuilderSupplier,
} from "./types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type RangeStatus = "draft" | "pending" | "approved" | "live" | "rejected";

interface Colour {
  name: string;
  hex: string;
  availability: "Ex-Stock" | "Made to Order" | "Stocked";
  /** Kept from modular schema — image of just this colour swatch, used by QB. */
  frontalImage?: string;
}

interface RangeImage {
  url: string;
  colourName?: string;
}

interface PaintToOrder {
  enabled: boolean;
  description: string;
}

interface SpecsBlob {
  material?: string;
  finishType?: string;
  finish?: string;
  thicknessMm?: number | string;
  frameWidthMm?: number | string;
  availability?: string;
  [k: string]: unknown;
}

type ProductCategoryKey = "Doors";

interface SupplierDirectoryResponse {
  ok?: boolean;
  suppliers?: BuilderSupplier[];
  error?: string;
}

interface DoorRange {
  id: string;
  supplierId?: string;
  supplierName?: string;
  /** PG suppliers.id when the slug is a reference entity, else null. */
  supplierUuid?: string | null;
  /** Provenance — 'admin' | 'supplier' | 'retailer'. Display only. */
  authoredBy?: string;
  updatedBy?: string;
  productCategory: ProductCategoryKey | string;
  status: RangeStatus;
  name: string;
  sku: string;
  description: string;
  category: string;
  subCategories?: string[];
  styleTags?: string[];
  finishType?: string;
  priceGroup: string;
  material?: string;
  colours: Colour[];
  heroImage: string;
  heroImageColour?: string;
  portfolioImages?: RangeImage[];
  doorCloseUps?: RangeImage[];
  doorCloseUp?: string;
  paintToOrder?: PaintToOrder;
  specs?: SpecsBlob;
  thicknessMm?: number | string;
  frameWidthMm?: number | string;
  availability?: string;
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ListResponse {
  ok?: boolean;
  ranges?: DoorRange[];
  error?: string;
}

interface OneResponse {
  ok?: boolean;
  range?: DoorRange;
  error?: string;
}

interface UploadResponse {
  ok?: boolean;
  urls?: string[];
  error?: string;
}

// ── Categories schema (stage line 9415) ──────────────────────────────────────

interface CategorySpec {
  key: string;
  label: string;
  placeholder?: string;
  type?: "text" | "number" | "select";
  options?: string[];
}

interface CategoryDef {
  icon: string;
  subCats: string[];
  specs: CategorySpec[];
}

const CATEGORIES: Record<string, CategoryDef> = {
  Doors: {
    icon: "▦",
    subCats: ["Traditional", "Contemporary", "Contour", "Modern", "Shaker", "Slab", "J Pull", "Handleless", "Inframe"],
    specs: [
      { key: "material",     label: "Material / Construction", placeholder: "e.g. 5-piece solid ash frame" },
      { key: "finish",       label: "Finish",                  placeholder: "Select finish...", type: "select",
        options: ["Gloss", "Matt", "Painted", "Wood Grain", "Metallic", "Foil"] },
      { key: "finishType",   label: "Finish Type (Details)",   placeholder: "e.g. Smooth Matt Painted, Woodgrain Foil" },
      { key: "thicknessMm",  label: "Thickness (mm)",          placeholder: "20",  type: "number" },
      { key: "frameWidthMm", label: "Frame Width (mm)",        placeholder: "105", type: "number" },
    ],
  },
};

/* Mirrors the server's own cap in modules/door_ranges.js:123. Kept in step so
 * the UI refuses the 16th tag instead of the save silently discarding it. */
const SUB_CAT_MAX = 15;

const PRICE_GROUPS = ["£", "££", "£££", "££££", "£££££"] as const;
const SPEC_BACKCOMPAT_KEYS = ["material", "finishType", "thicknessMm", "frameWidthMm", "availability"] as const;

const STATUS_TONE: Record<RangeStatus, string> = {
  draft:    "dim",
  pending:  "amber",
  approved: "green",
  live:     "green",
  rejected: "red",
};

const UPLOAD_ENDPOINT = "/api/door-ranges/upload-image";

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function attr(s: unknown): string { return esc(s).replace(/`/g, "&#96;"); }

function normImgArray(arr: unknown): RangeImage[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((e): RangeImage => {
      if (e && typeof e === "object") {
        const obj = e as Record<string, unknown>;
        return { url: String(obj["url"] ?? ""), colourName: String(obj["colourName"] ?? "") };
      }
      return { url: String(e ?? ""), colourName: "" };
    })
    .filter((e) => !!e.url);
}

function formatDateGB(v: string | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Inline toast (top-right) ─────────────────────────────────────────────────

function toast(msg: string, isErr = false): void {
  const t = document.createElement("div");
  t.className = "dr-toast" + (isErr ? " err" : "");
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3500);
}

// ── drDialog — multi-field modal with live hex preview (stage line 9537) ────

interface DialogField {
  label: string;
  placeholder?: string;
  value?: string;
  type?: "text" | "select";
  options?: string[];
  hexPreview?: boolean;
}
interface DialogOptions {
  title: string;
  body?: string;
  fields?: DialogField[];
  confirmLabel?: string;
  cancelLabel?: string | false;
  danger?: boolean;
}
interface DialogResult { confirmed: boolean; values: string[] }

function drDialog(opts: DialogOptions): Promise<DialogResult | null> {
  return new Promise((resolve) => {
    const ov = document.createElement("div");
    ov.className = "dr-dlg-ov";
    const fieldsHtml = (opts.fields || []).map((f, i) => {
      const inputHtml = f.type === "select"
        ? `<select id="dr-dlg-f-${i}">${(f.options || []).map((o) => `<option value="${attr(o)}" ${o === f.value ? "selected" : ""}>${esc(o)}</option>`).join("")}</select>`
        : `<input type="text" id="dr-dlg-f-${i}" value="${attr(f.value ?? "")}" placeholder="${attr(f.placeholder ?? "")}">`;
      const previewHtml = f.hexPreview
        ? `<div class="dr-hex-preview"><div class="dot" id="dr-dlg-dot-${i}" style="background:${attr(f.value || "#888")}"></div><span class="hint">Live preview</span></div>`
        : "";
      return `<div class="field"><label>${esc(f.label)}</label>${inputHtml}${previewHtml}</div>`;
    }).join("");
    ov.innerHTML = `
      <div class="dr-dlg">
        <div class="dr-dlg-h">${esc(opts.title)}</div>
        <div class="dr-dlg-b">
          ${opts.body ? `<p>${esc(opts.body)}</p>` : ""}
          ${fieldsHtml}
        </div>
        <div class="dr-dlg-f">
          ${opts.cancelLabel !== false ? `<button class="dr-btn" id="dr-dlg-cancel">${esc(opts.cancelLabel ?? "Cancel")}</button>` : ""}
          <button class="dr-btn ${opts.danger ? "danger" : "primary"}" id="dr-dlg-ok">${esc(opts.confirmLabel ?? "OK")}</button>
        </div>
      </div>
    `;
    document.body.appendChild(ov);

    (opts.fields || []).forEach((f, i) => {
      if (!f.hexPreview) return;
      const inp = ov.querySelector<HTMLInputElement>(`#dr-dlg-f-${i}`);
      const dot = ov.querySelector<HTMLElement>(`#dr-dlg-dot-${i}`);
      if (!inp || !dot) return;
      inp.addEventListener("input", () => {
        const v = inp.value.trim();
        if (/^#[0-9a-fA-F]{3,8}$/.test(v)) dot.style.background = v;
      });
    });

    const done = (confirmed: boolean): void => {
      const values = (opts.fields || []).map((_, i) => {
        const inp = ov.querySelector<HTMLInputElement | HTMLSelectElement>(`#dr-dlg-f-${i}`);
        return inp ? inp.value.trim() : "";
      });
      ov.remove();
      resolve(confirmed ? { confirmed: true, values } : null);
    };
    ov.querySelector<HTMLButtonElement>("#dr-dlg-ok")?.addEventListener("click", () => done(true));
    ov.querySelector<HTMLButtonElement>("#dr-dlg-cancel")?.addEventListener("click", () => done(false));
    ov.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); done(true); }
      else if (e.key === "Escape") done(false);
    });
    setTimeout(() => ov.querySelector<HTMLInputElement>("input,select")?.focus(), 50);
  });
}

// ── Dominant-colour extraction (stage line 9592) ─────────────────────────────

function extractDominantColour(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = (): void => {
      try {
        const SIZE = 40;
        const cvs = document.createElement("canvas");
        cvs.width = SIZE; cvs.height = SIZE;
        const ctx = cvs.getContext("2d");
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const data = ctx.getImageData(0, 0, SIZE, SIZE).data;
        const pixels: number[][] = [];
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i] ?? 0, g = data[i + 1] ?? 0, b = data[i + 2] ?? 0, a = data[i + 3] ?? 0;
          if (a < 128) continue;
          const br = (r + g + b) / 3;
          if (br > 240 || br < 15) continue;
          pixels.push([r, g, b]);
        }
        if (pixels.length === 0) { resolve(null); return; }
        const K = 3;
        let centres = pixels.slice(0, K).map((p) => p.slice());
        for (let iter = 0; iter < 8; iter++) {
          const clusters: number[][][] = centres.map(() => []);
          pixels.forEach((p) => {
            let best = 0, bestD = Infinity;
            centres.forEach((c, ci) => {
              const d = (p[0]! - c[0]!) ** 2 + (p[1]! - c[1]!) ** 2 + (p[2]! - c[2]!) ** 2;
              if (d < bestD) { bestD = d; best = ci; }
            });
            clusters[best]!.push(p);
          });
          centres = clusters.map((cl, ci) => {
            if (cl.length === 0) return centres[ci]!;
            const sum = [0, 0, 0];
            cl.forEach((p) => { sum[0]! += p[0]!; sum[1]! += p[1]!; sum[2]! += p[2]!; });
            return [Math.round(sum[0]! / cl.length), Math.round(sum[1]! / cl.length), Math.round(sum[2]! / cl.length)];
          });
        }
        const counts = [0, 0, 0];
        pixels.forEach((p) => {
          let best = 0, bestD = Infinity;
          centres.forEach((c, ci) => {
            const d = (p[0]! - c[0]!) ** 2 + (p[1]! - c[1]!) ** 2 + (p[2]! - c[2]!) ** 2;
            if (d < bestD) { bestD = d; best = ci; }
          });
          counts[best]!++;
        });
        const idx = counts.indexOf(Math.max(...counts));
        const dom = centres[idx]!;
        resolve("#" + dom.map((v) => ("0" + v.toString(16)).slice(-2)).join(""));
      } catch { resolve(null); }
    };
    img.onerror = (): void => resolve(null);
    img.src = url;
  });
}

// ── File upload to /api/door-ranges/upload-image ────────────────────────────

function drUploadFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("files", file);
    fetch(UPLOAD_ENDPOINT, { method: "POST", body: fd, credentials: "include" })
      .then((r) => r.json() as Promise<UploadResponse>)
      .then((data) => {
        if (data.urls && data.urls[0]) resolve(data.urls[0]);
        else reject(new Error(data.error ?? "Upload failed"));
      })
      .catch(reject);
  });
}

// ── Drag/drop zone (stage line 9689) ─────────────────────────────────────────

interface DzZone {
  el: HTMLDivElement;
  setUrl: (url: string) => void;
}

function makeDzZone(initialUrl: string, onUrlChange: (url: string) => void): DzZone {
  const zone = document.createElement("div");
  zone.className = "dr-drop-zone" + (initialUrl ? " has-image" : "");

  const preview = document.createElement("div");
  preview.className = "dz-preview";
  if (initialUrl) preview.style.backgroundImage = `url(${initialUrl})`;

  const hint = document.createElement("div");
  hint.className = "dz-hint";
  hint.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" fill="none" stroke-width="2" style="display:block;margin:0 auto 2px"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>Drop or click`;
  if (initialUrl) hint.style.display = "none";

  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = "image/*";
  fileInput.title = "Click to choose or drag an image here";

  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.className = "dz-clear";
  clearBtn.title = "Remove image";
  clearBtn.innerHTML = "&times;";
  clearBtn.style.display = initialUrl ? "" : "none";

  zone.appendChild(preview);
  zone.appendChild(hint);
  zone.appendChild(fileInput);
  zone.appendChild(clearBtn);

  function setUrl(url: string): void {
    if (url) {
      preview.style.backgroundImage = `url(${url})`;
      hint.style.display = "none";
      clearBtn.style.display = "";
      zone.classList.add("has-image");
    } else {
      preview.style.backgroundImage = "";
      hint.style.display = "";
      clearBtn.style.display = "none";
      zone.classList.remove("has-image");
    }
    onUrlChange(url);
  }

  function handleFile(file: File): void {
    if (!file || !file.type.startsWith("image/")) return;
    const spin = document.createElement("div");
    spin.className = "dz-spinner";
    spin.innerHTML = `<div class="dz-spin"></div>`;
    zone.appendChild(spin);
    drUploadFile(file)
      .then((url) => { spin.remove(); setUrl(url); toast("Image uploaded"); })
      .catch((err: Error) => { spin.remove(); toast(`Upload failed: ${err.message}`, true); });
  }

  fileInput.addEventListener("change", () => {
    if (fileInput.files && fileInput.files[0]) handleFile(fileInput.files[0]);
  });
  clearBtn.addEventListener("click", (e) => { e.stopPropagation(); setUrl(""); });
  zone.addEventListener("dragover", (e) => { e.preventDefault(); zone.classList.add("drag-over"); });
  zone.addEventListener("dragleave", () => zone.classList.remove("drag-over"));
  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    zone.classList.remove("drag-over");
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  });

  return { el: zone, setUrl };
}

// ── Image-rows + flush (stage line 9763) ─────────────────────────────────────

function renderImgRows(
  container: HTMLElement,
  imgArray: RangeImage[],
  addLabel: string,
  onRemove: (idx: number) => void,
  onExtract: ((url: string, idx: number, row: HTMLElement) => void) | null,
): void {
  container.innerHTML = "";
  imgArray.forEach((img, i) => {
    const row = document.createElement("div");
    row.className = "dr-img-row";

    const dz = makeDzZone(img.url, (url) => {
      img.url = url;
      const inp = row.querySelector<HTMLInputElement>('input[data-dr-url]');
      if (inp) inp.value = url;
      if (url && onExtract) onExtract(url, i, row);
    });
    dz.el.style.cssText = "width:72px;height:52px;flex-shrink:0;";
    const hintEl = dz.el.querySelector<HTMLElement>(".dz-hint");
    if (hintEl) hintEl.style.fontSize = "9px";

    const urlInput = document.createElement("input");
    urlInput.type = "text";
    urlInput.setAttribute("data-dr-url", "1");
    urlInput.placeholder = "Paste URL or drop image above";
    urlInput.value = img.url;

    let extractTimer: number | null = null;
    urlInput.addEventListener("input", () => {
      img.url = urlInput.value.trim();
      const prev = dz.el.querySelector<HTMLElement>(".dz-preview");
      if (prev) prev.style.backgroundImage = img.url ? `url(${img.url})` : "";
      if (img.url) {
        dz.el.classList.add("has-image");
        const h = dz.el.querySelector<HTMLElement>(".dz-hint"); if (h) h.style.display = "none";
        const c = dz.el.querySelector<HTMLElement>(".dz-clear"); if (c) c.style.display = "";
      } else {
        dz.el.classList.remove("has-image");
        const h = dz.el.querySelector<HTMLElement>(".dz-hint"); if (h) h.style.display = "";
        const c = dz.el.querySelector<HTMLElement>(".dz-clear"); if (c) c.style.display = "none";
      }
      if (extractTimer != null) window.clearTimeout(extractTimer);
      if (img.url && onExtract) extractTimer = window.setTimeout(() => onExtract(img.url, i, row), 800);
    });

    const colInput = document.createElement("input");
    colInput.type = "text";
    colInput.setAttribute("data-dr-colour", "1");
    colInput.placeholder = "Colour / finish name";
    colInput.value = img.colourName ?? "";
    colInput.addEventListener("input", () => { img.colourName = colInput.value.trim(); });

    const rmBtn = document.createElement("button");
    rmBtn.type = "button";
    rmBtn.className = "dr-rm-btn";
    rmBtn.title = "Remove";
    rmBtn.innerHTML = "&times;";
    rmBtn.addEventListener("click", () => onRemove(i));

    row.appendChild(dz.el);
    row.appendChild(urlInput);
    row.appendChild(colInput);
    row.appendChild(rmBtn);
    container.appendChild(row);
  });

  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "dr-img-add";
  addBtn.innerHTML = `+ ${esc(addLabel)}`;
  addBtn.addEventListener("click", () => {
    imgArray.push({ url: "", colourName: "" });
    renderImgRows(container, imgArray, addLabel, onRemove, onExtract);
    const rows = container.querySelectorAll<HTMLElement>(".dr-img-row");
    rows[rows.length - 1]?.querySelector<HTMLInputElement>('input[data-dr-url]')?.focus();
  });
  container.appendChild(addBtn);
}

function flushImgRows(container: HTMLElement, imgArray: RangeImage[]): void {
  container.querySelectorAll<HTMLElement>(".dr-img-row").forEach((row, i) => {
    const e = imgArray[i];
    if (!e) return;
    const urlInp = row.querySelector<HTMLInputElement>("input[data-dr-url]");
    const colInp = row.querySelector<HTMLInputElement>("input[data-dr-colour]");
    if (urlInp) e.url = urlInp.value.trim();
    if (colInp) e.colourName = colInp.value.trim();
  });
}

// ── Colour grid (stage line 9651) ────────────────────────────────────────────

function renderColourGrid(
  container: HTMLElement,
  colours: Colour[],
  onAdd: () => void,
  onRemove: (idx: number) => void,
): void {
  container.innerHTML = "";
  colours.forEach((c, i) => {
    const chip = document.createElement("div");
    chip.className = "dr-colour-chip";
    chip.innerHTML = `
      <div class="swatch" style="background:${attr(c.hex || "#888")}"></div>
      <div class="name">${esc(c.name)}</div>
      <button type="button" class="rm" title="Remove">&times;</button>
    `;
    chip.querySelector<HTMLButtonElement>(".rm")?.addEventListener("click", () => onRemove(i));
    container.appendChild(chip);
  });
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "dr-colour-add-btn";
  addBtn.textContent = "+ Add";
  addBtn.addEventListener("click", onAdd);
  container.appendChild(addBtn);
}

// ── Page ─────────────────────────────────────────────────────────────────────

/* Sentinel for the picker's "+ Add new supplier" entry. Not a slug — slugify()
 * strips underscores, so no real supplier can ever collide with it. */
const ADD_SUPPLIER_VALUE = "__add_supplier__";

export function createDoorRangeBuilderPage(opts: BuilderOptions): BuilderPage {
  const isAdminScope = opts.scope.kind === "admin";

  const page: BuilderPage = {
  name: opts.name ?? "door-ranges",
  label: opts.label ?? "Door Ranges",
  icon: opts.icon ?? "🚪",
  ...(opts.section ? { section: opts.section } : {}),

  mount(container: HTMLElement, ctx: BuilderContext): BuilderCleanup {
    let products: DoorRange[] = [];
    let listFilter: string = "All";
    const ac = new AbortController();

    /* Admin scope only: which supplier's catalogue is being edited. In "self"
       scope this stays null and the server resolves identity from the cookie. */
    let suppliers: BuilderSupplier[] = [];
    let selected: BuilderSupplier | null = null;

    container.innerHTML = `
      <section class="dr-root">
        ${isAdminScope ? `<div class="dr-card dr-scope-card" data-role="scope-card"></div>` : ""}
        <div class="dr-card" data-role="list-card"></div>
      </section>
    `;

    const listCard = container.querySelector<HTMLElement>("[data-role='list-card']")!;
    const scopeCard = container.querySelector<HTMLElement>("[data-role='scope-card']");

    // ── Supplier picker (admin scope only) ───────────────────────────────
    // Options come from /api/admin/supplier-directory, which merges PG
    // `suppliers` with the file-store accounts. Adding a supplier row there
    // makes it appear here with no code change — the same dynamic-columns
    // property the catalogue-access matrix already relies on.
    function renderScopeCard(): void {
      if (!scopeCard) return;
      const optsHtml = suppliers.map((s) => {
        const tag = s.canSelfServe ? "has own login" : "managed centrally";
        return `<option value="${attr(s.slug)}"${selected && s.slug === selected.slug ? " selected" : ""}>${esc(s.name)} — ${esc(tag)}</option>`;
      }).join("");
      // Shortcut to the platform's real supplier-creation flow. It sits at the
      // top because it is the thing you reach for when the supplier you want
      // is missing — i.e. after scanning the list and not finding it.
      const addHtml = opts.onAddSupplier
        ? `<option value="${ADD_SUPPLIER_VALUE}">+ Add new supplier…</option>`
        : "";
      scopeCard.innerHTML = `
        <div class="dr-scope">
          <div class="dr-scope-label">
            <span class="dr-scope-title">Editing catalogue for</span>
            <span class="dr-sub">Ranges you save here are attributed to the chosen supplier, not to you.</span>
          </div>
          <select class="dr-scope-select" data-role="supplier-select" aria-label="Supplier to edit">
            <option value="">Select a supplier…</option>
            ${addHtml}
            ${optsHtml}
          </select>
        </div>
        <div data-role="brand-host"></div>
      `;
      scopeCard.querySelector<HTMLSelectElement>("[data-role='supplier-select']")
        ?.addEventListener("change", (e) => {
          const el = e.target as HTMLSelectElement;
          const slug = el.value;
          if (slug === ADD_SUPPLIER_VALUE) {
            // An action, not a scope. Put the visible value back before handing
            // off, so returning to this page doesn't show "+ Add new supplier"
            // sitting there as though it were the supplier being edited.
            el.value = selected ? selected.slug : "";
            opts.onAddSupplier?.();
            return;
          }
          selected = suppliers.find((s) => s.slug === slug) ?? null;
          // The brand control's visibility and contents both depend on
          // `selected`, so it has to re-render here too. Its own host, not the
          // whole scope card: rebuilding the card would destroy the <select>
          // that is mid-change-event and throw away keyboard focus.
          renderBrandControl();
          renderListShell();
          void refreshList();
        });

      renderBrandControl();
    }

    /**
     * "Name customers see" — the consumer-facing brand (suppliers.display_name).
     *
     * Only offered once a supplier is chosen, and only for suppliers that have
     * a PG catalogue record to carry the column; an account-only supplier has
     * no row, so the PATCH would 404.
     */
    function renderBrandControl(): void {
      const host = scopeCard?.querySelector<HTMLElement>("[data-role='brand-host']");
      if (!host) return;
      const target = selected;
      if (!target || !target.uuid) {
        host.innerHTML = "";
        return;
      }
      host.innerHTML = `
        <div class="dr-scope-brand">
          <label class="dr-scope-brand-label" for="drBrandName">
            Name customers see
            <span class="dr-sub">Shown on the style step of the wizard. ${esc(target.name)}'s
            own record name doesn't change — this is just the collection's brand.</span>
          </label>
          <div class="dr-scope-brand-row">
            <input id="drBrandName" class="dr-input" type="text" maxlength="120"
                   value="${attr(target.displayName ?? "")}"
                   placeholder="${attr(target.slug.toUpperCase())} (default)"
                   autocomplete="off" />
            <button type="button" class="dr-btn" data-role="brand-save">Save</button>
          </div>
        </div>
      `;

      const input = host.querySelector<HTMLInputElement>("#drBrandName");
      // Narrow PATCH — display_name only. It deliberately cannot reach `name`
      // or `slug`, which order matching, notification routing and reports all
      // depend on staying put.
      const save = (): void => {
        if (!input) return;
        const next = input.value.trim();
        if (next === (target.displayName ?? "")) return;   // nothing to save
        input.disabled = true;
        void ctx
          .api(`/admin/suppliers/${encodeURIComponent(target.slug)}/display-name`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            // Empty clears it, restoring the default label.
            body: JSON.stringify({ displayName: next || null }),
          })
          .then(() => {
            target.displayName = next || null;
            toast(next
              ? `Customers will see “${next}”`
              : `Reset to the default name`);
            renderBrandControl();
          })
          .catch((err) => {
            if (isAbortError(err)) return;
            input.disabled = false;
            toast("Could not save that name", true);
          });
      };
      host.querySelector<HTMLButtonElement>("[data-role='brand-save']")
        ?.addEventListener("click", save);
      input?.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        save();
      });
    }

    async function loadSuppliers(): Promise<void> {
      if (!isAdminScope) return;
      try {
        const resp = await ctx.api<SupplierDirectoryResponse>("/admin/supplier-directory", { signal: ac.signal });
        suppliers = Array.isArray(resp?.suppliers) ? resp.suppliers : [];
      } catch (err) {
        if (isAbortError(err)) return;
        suppliers = [];
        toast("Could not load the supplier list", true);
      }
      renderScopeCard();
    }

    function renderListShell(): void {
      const pillsHtml = ["All", ...Object.keys(CATEGORIES)].map((c) =>
        `<button class="dr-cat-pill${c === listFilter ? " active" : ""}" data-fcat="${attr(c)}">${esc(c)}</button>`
      ).join("");
      // Admin is editing someone else's catalogue, so "My" would be wrong, and
      // "+ Add" must stay disabled until a supplier is chosen — otherwise the
      // create would have no supplier to attribute to.
      const heading = isAdminScope
        ? (selected ? `${esc(selected.name)} — Product Catalogue` : "Product Catalogue")
        : "My Product Catalogue";
      const sub = isAdminScope
        ? (selected
            ? `Maintaining ${esc(selected.name)}'s ranges on their behalf.`
            : "Choose a supplier above to view and edit their ranges.")
        : "Manage every product type you supply — doors, handles, sinks, taps, worktops and more.";
      const addDisabled = isAdminScope && !selected;
      listCard.innerHTML = `
        <div class="dr-card-h">
          <div>
            <h3>${heading}</h3>
            <span class="dr-sub">${sub}</span>
          </div>
          <button class="dr-btn primary" data-role="new"${addDisabled ? " disabled" : ""}>+ Add new product</button>
        </div>
        <div class="dr-filter-row">
          <div class="dr-cat-pills" data-role="filter-pills">${pillsHtml}</div>
        </div>
        <div class="dr-table-wrap">
          <table class="dr-table">
            <thead>
              <tr>
                <th>Product</th><th>Category</th><th>SKU</th><th>Price Group</th>
                <th>Colours</th><th>Images</th><th>Status</th><th>Updated</th><th></th>
              </tr>
            </thead>
            <tbody data-role="list">
              <tr><td colspan="9" class="dr-empty">Loading…</td></tr>
            </tbody>
          </table>
        </div>
      `;
      listCard.querySelector<HTMLButtonElement>("[data-role='new']")?.addEventListener("click", () => openProductModal(null));
      listCard.querySelector<HTMLElement>("[data-role='filter-pills']")?.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-fcat]");
        if (!btn) return;
        listFilter = btn.dataset["fcat"] ?? "All";
        listCard.querySelectorAll<HTMLButtonElement>("[data-fcat]").forEach((b) => {
          b.classList.toggle("active", b.dataset["fcat"] === listFilter);
        });
        renderList();
      });
    }

    function renderList(): void {
      const tbody = listCard.querySelector<HTMLElement>("[data-role='list']");
      if (!tbody) return;
      const visible = listFilter === "All"
        ? products
        : products.filter((p) => (p.productCategory || "Doors") === listFilter);
      if (visible.length === 0) {
        tbody.innerHTML = `<tr><td colspan="9" class="dr-empty">No products in this category yet — click "Add new product" to get started.</td></tr>`;
        return;
      }
      tbody.innerHTML = visible.map((r) => {
        const thumb = r.heroImage
          ? `<div class="dr-thumb" style="background-image:url(${attr(r.heroImage)})"></div>`
          : `<div class="dr-thumb dr-thumb--empty"></div>`;
        const pc = r.productCategory || "Doors";
        const pg = r.priceGroup || "—";
        const col = Array.isArray(r.colours) ? r.colours.length : 0;
        const imgs =
          (Array.isArray(r.portfolioImages) ? r.portfolioImages.length : 0) +
          (Array.isArray(r.doorCloseUps) ? r.doorCloseUps.length : 0) +
          (r.heroImage ? 1 : 0);
        const updated = formatDateGB(r.updatedAt);
        const tone = STATUS_TONE[r.status] ?? "dim";
        return `
          <tr>
            <td><div class="dr-prod-cell">${thumb}<span class="dr-prod-name">${esc(r.name)}</span></div></td>
            <td><span class="dr-pill dim">${esc(pc)}</span></td>
            <td class="dr-mono">${esc(r.sku || "—")}</td>
            <td><span class="dr-pg">${esc(pg)}</span></td>
            <td>${col}</td>
            <td>${imgs}</td>
            <td><span class="dr-pill ${tone}">${esc(r.status)}</span></td>
            <td class="dr-mono dim">${esc(updated)}</td>
            <td class="dr-actions-cell">
              <div class="dr-actions">
                <button class="dr-btn sm" data-action="edit" data-rid="${attr(r.id)}">Edit</button>
                <button class="dr-btn sm danger" data-action="delete" data-rid="${attr(r.id)}" data-rname="${attr(r.name)}">Delete</button>
              </div>
            </td>
          </tr>
        `;
      }).join("");

      tbody.querySelectorAll<HTMLButtonElement>("[data-action='edit']").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          openProductModal(btn.dataset["rid"] ?? null);
        });
      });
      tbody.querySelectorAll<HTMLButtonElement>("[data-action='delete']").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          e.stopPropagation();
          const id = btn.dataset["rid"];
          const name = btn.dataset["rname"] ?? "";
          if (!id) return;
          const res = await drDialog({
            title: "Delete Product",
            body: `Are you sure you want to delete "${name}"? This cannot be undone.`,
            confirmLabel: "Delete",
            danger: true,
          });
          if (!res || !res.confirmed) return;
          try {
            await ctx.api<OneResponse>(`/door-ranges/${encodeURIComponent(id)}`, { method: "DELETE" });
            toast("Product deleted");
            await refreshList();
          } catch (err) {
            toast(err instanceof Error ? err.message : "Delete failed", true);
          }
        });
      });
    }

    async function refreshList(): Promise<void> {
      // Admin sees everything by default, so it MUST narrow to the selected
      // supplier — otherwise the list would mix every supplier's ranges
      // together. With nothing selected there is nothing meaningful to show.
      if (isAdminScope && !selected) {
        products = [];
        const tbody = listCard.querySelector<HTMLElement>("[data-role='list']");
        if (tbody) {
          tbody.innerHTML = `<tr><td colspan="9" class="dr-empty">Select a supplier to see their ranges.</td></tr>`;
        }
        return;
      }
      const path = isAdminScope && selected
        ? `/door-ranges?supplierId=${encodeURIComponent(selected.slug)}`
        : "/door-ranges";
      try {
        const resp = await ctx.api<ListResponse>(path, { signal: ac.signal });
        products = Array.isArray(resp?.ranges) ? resp.ranges : [];
        renderList();
      } catch (err) {
        if (isAbortError(err)) return;
        const tbody = listCard.querySelector<HTMLElement>("[data-role='list']");
        if (tbody) {
          tbody.innerHTML = `<tr><td colspan="9" class="dr-empty err">Failed to load — ${esc(err instanceof Error ? err.message : "unknown")}</td></tr>`;
        }
      }
    }

    // ── Product modal ────────────────────────────────────────────────────
    function openProductModal(id: string | null): void {
      const src = id ? products.find((x) => x.id === id) ?? null : null;
      const isEdit = !!(id && src);

      const current: DoorRange = src
        ? { ...src }
        : {
            id: "",
            productCategory: "Doors",
            name: "",
            sku: "",
            status: "draft",
            description: "",
            category: "",
            priceGroup: "",
            heroImage: "",
            heroImageColour: "",
            colours: [],
            specs: {},
            styleTags: [],
            paintToOrder: { enabled: false, description: "" },
          };
      if (!current.productCategory) current.productCategory = "Doors";
      if (!current.paintToOrder) current.paintToOrder = { enabled: false, description: "" };

      // Working copies
      const coloursCopy: Colour[] = (current.colours ?? []).map((c) => ({ ...c }));
      const portfolioCopy: RangeImage[] = normImgArray(current.portfolioImages);
      const closeUpsCopy: RangeImage[] = normImgArray(
        Array.isArray(current.doorCloseUps) && current.doorCloseUps.length > 0
          ? current.doorCloseUps
          : (current.doorCloseUp ? [{ url: current.doorCloseUp, colourName: "" }] : [])
      );
      const specsCopy: SpecsBlob = { ...(current.specs ?? {}) };
      // Legacy back-compat: lift top-level fields into specs jsonb on load.
      (["material", "finishType", "thicknessMm", "frameWidthMm", "availability"] as const).forEach((k) => {
        const v = (current as unknown as Record<string, unknown>)[k];
        if (v != null && v !== "" && specsCopy[k] == null) specsCopy[k] = v as never;
      });

      const subCatsCopy: string[] = Array.isArray(current.subCategories) && current.subCategories.length > 0
        ? current.subCategories.slice()
        : (current.category ? [current.category] : []);

      const ov = document.createElement("div");
      ov.className = "dr-ov";

      /* Tags compare case-insensitively so a typed "shaker" can't end up
       * sitting next to the picked "Shaker". */
      function hasSubCat(v: string): boolean {
        const k = v.trim().toLowerCase();
        return subCatsCopy.some((t) => t.toLowerCase() === k);
      }

      /* A typed value matching a predefined sub-category adopts that entry's
       * casing, so free text can't quietly fork "J Pull" into "j pull". */
      function canonicalSubCat(v: string, pc: string): string {
        const subs = (CATEGORIES[pc] ?? CATEGORIES["Doors"]!).subCats;
        const k = v.trim().toLowerCase();
        return subs.find((s) => s.toLowerCase() === k) ?? v.trim();
      }

      function addSubCat(raw: string): void {
        const pc  = String(current.productCategory);
        const val = canonicalSubCat(raw, pc);
        if (!val || hasSubCat(val)) return;
        // The server keeps at most 15 (modules/door_ranges.js:123). Stop here
        // rather than let it silently drop the overflow on save.
        if (subCatsCopy.length >= SUB_CAT_MAX) {
          toast(`You can add up to ${SUB_CAT_MAX} sub-categories.`, true);
          return;
        }
        subCatsCopy.push(val);
        const picker = ov.querySelector<HTMLElement>("#drSubCatPicker");
        if (!picker) return;
        picker.innerHTML = buildSubCatPicker(pc);
        wireSubCatPicker();
      }

      function buildSubCatPicker(pc: string): string {
        const subs = (CATEGORIES[pc] ?? CATEGORIES["Doors"]!).subCats;
        const tagsHtml = subCatsCopy.map((t, i) =>
          `<span class="dr-subcat-tag">${esc(t)}<button type="button" data-sc-rm="${i}" aria-label="Remove">&times;</button></span>`
        ).join("");
        const optionsHtml = subs.filter((s) => !hasSubCat(s)).map((s) =>
          `<option value="${attr(s)}">${esc(s)}</option>`
        ).join("");
        // The predefined list doesn't cover every range, so the value is also
        // free text. Typed entries become identical tags — the list is a
        // convenience, not a constraint. maxlength matches the server's own
        // 60-char cap so nothing is truncated after the fact.
        return `
          <div class="dr-subcat-tags" id="drSubCatTags">${tagsHtml}</div>
          <div class="dr-subcat-row">
            <select id="drSubCatSelect" class="dr-input">
              <option value="">+ Add sub-category…</option>
              ${optionsHtml}
            </select>
            <input id="drSubCatCustom" class="dr-input" type="text" maxlength="60"
                   placeholder="Or type your own…" autocomplete="off"
                   aria-label="Add a custom sub-category" />
            <button type="button" class="dr-btn" id="drSubCatAdd">Add</button>
          </div>
        `;
      }

      function buildCategoryPills(): string {
        return Object.keys(CATEGORIES).map((c) =>
          `<button type="button" class="dr-cat-pill${current.productCategory === c ? " active" : ""}" data-pcat="${attr(c)}">${esc(c)}</button>`
        ).join("");
      }

      function buildSpecsHtml(pc: string): string {
        const specs = (CATEGORIES[pc] ?? CATEGORIES["Doors"]!).specs;
        const rows: string[] = [];
        for (let i = 0; i < specs.length; i += 2) {
          const s1 = specs[i], s2 = specs[i + 1];
          rows.push(`<div class="dr-row-2">${renderSpecField(s1)}${renderSpecField(s2)}</div>`);
        }
        return rows.join("");
      }

      function renderSpecField(s: CategorySpec | undefined): string {
        if (!s) return `<div class="dr-field"></div>`;
        const val = String(specsCopy[s.key] ?? "");
        const inputHtml = s.type === "select"
          ? `<select data-spec-key="${attr(s.key)}" class="dr-input">
              <option value="">${esc(s.placeholder ?? "Select...")}</option>
              ${(s.options ?? []).map((o) =>
                `<option value="${attr(o)}" ${val === o ? "selected" : ""}>${esc(o)}</option>`
              ).join("")}
            </select>`
          : `<input class="dr-input" type="${s.type ?? "text"}" data-spec-key="${attr(s.key)}" value="${attr(val)}" placeholder="${attr(s.placeholder ?? "")}">`;
        return `<div class="dr-field"><label>${esc(s.label)}</label>${inputHtml}</div>`;
      }

      function flushSpecsBox(): void {
        ov.querySelectorAll<HTMLInputElement | HTMLSelectElement>("#drSpecsBox [data-spec-key]").forEach((el) => {
          const k = el.dataset["specKey"];
          if (k) specsCopy[k] = el.value.trim();
        });
      }

      ov.innerHTML = `
        <div class="dr-md">
          <div class="dr-md-h">
            <h3>${isEdit ? `Edit Product — ${esc(current.name)}` : "Add New Product"}</h3>
            <button class="dr-md-x" id="drMdClose" aria-label="Close">&times;</button>
          </div>
          <div class="dr-md-b">

            <div class="dr-field">
              <label>Product Category</label>
              <div class="dr-cat-pills" id="drProductCatPills">${buildCategoryPills()}</div>
              <input type="hidden" id="drProductCategory" value="${attr(current.productCategory)}">
            </div>

            <div class="dr-row-2">
              <div class="dr-field">
                <label>Product Name</label>
                <input class="dr-input" type="text" id="drName" value="${attr(current.name)}" placeholder="e.g. Cambridge / Brushed Brass T-Bar">
              </div>
              <div class="dr-field">
                <label>SKU <span class="dr-hint">(your internal product code)</span></label>
                <input class="dr-input" type="text" id="drSku" value="${attr(current.sku)}" placeholder="e.g. CAM-001-IVY">
              </div>
            </div>

            <div class="dr-field">
              <label>Description</label>
              <textarea class="dr-input" id="drDesc" placeholder="Brief description shown to consumers in the wizard…">${esc(current.description)}</textarea>
            </div>

            <div class="dr-row-2">
              <div class="dr-field">
                <label>Sub-Category / Style <span class="dr-hint">(select one or more)</span></label>
                <div id="drSubCatPicker">${buildSubCatPicker(String(current.productCategory))}</div>
              </div>
              <div class="dr-field">
                <label>Price Group <span class="dr-hint">(optional)</span></label>
                <div class="dr-pg-row" id="drPgBtns">
                  ${PRICE_GROUPS.map((p) =>
                    `<button type="button" class="dr-btn sm dr-pg-btn${current.priceGroup === p ? " primary" : ""}" data-pg="${attr(p)}">${esc(p)}</button>`
                  ).join("")}
                </div>
                <input type="hidden" id="drPriceGroup" value="${attr(current.priceGroup)}">
                <button type="button" id="drPgClear" class="dr-link-btn">Clear selection</button>
              </div>
            </div>

            <div class="dr-section">
              <div class="dr-section-h">Specifications</div>
              <div id="drSpecsBox">${buildSpecsHtml(String(current.productCategory))}</div>
            </div>

            <div class="dr-section">
              <div class="dr-section-h">Images</div>

              <div class="dr-field">
                <label>1. Hero Image <span class="dr-hint">(main thumbnail in the wizard)</span></label>
                <div class="dr-hero-zone" id="drHeroZoneWrap">
                  <div id="drHeroDzSlot"></div>
                  <div class="dr-hero-inputs">
                    <input class="dr-input" type="text" id="drHero" value="${attr(current.heroImage)}" placeholder="Paste URL or drop image on the left">
                    <input class="dr-input" type="text" id="drHeroColour" value="${attr(current.heroImageColour ?? "")}" placeholder="Colour name (e.g. Ivory)">
                  </div>
                </div>
              </div>

              <div class="dr-field" style="margin-bottom:0">
                <label>2. Portfolio Images <span class="dr-hint">(lifestyle / showcase)</span></label>
                <p class="dr-section-hint">Optionally tag each with a colour/finish name.</p>
                <div id="drPortfolioList"></div>
              </div>

              <div class="dr-sep"></div>

              <div class="dr-field" style="margin-bottom:0">
                <label>3. Product Close-Up Images <span class="dr-hint">(detail shots)</span></label>
                <p class="dr-section-hint">Entering a URL auto-suggests a hex colour.</p>
                <div id="drCloseUpList"></div>
              </div>
            </div>

            <div class="dr-section">
              <div class="dr-section-h">Paint to Order / Bespoke</div>
              <div class="dr-pto-row">
                <input type="checkbox" id="drPtoEnabled" ${current.paintToOrder.enabled ? "checked" : ""}>
                <label for="drPtoEnabled">Available as Paint to Order / Bespoke</label>
              </div>
              <div id="drPtoDetails" style="display:${current.paintToOrder.enabled ? "block" : "none"}">
                <textarea class="dr-input" id="drPtoDesc" placeholder="e.g. 36 Paint to Order colours available, plus bespoke RAL matching...">${esc(current.paintToOrder.description)}</textarea>
              </div>
            </div>

            <div class="dr-field">
              <label id="drColourLabel">Colour / Finish Swatches (${coloursCopy.length})</label>
              <p class="dr-section-hint">Each swatch represents an available colour or finish for this product.</p>
              <div class="dr-colour-grid" id="drColourGrid"></div>
            </div>

          </div>
          <div class="dr-md-f">
            ${isEdit ? '<button class="dr-btn danger" id="drBtnDelete" data-role="delete-in-modal">Delete product</button>' : ""}
            <button class="dr-btn" id="drBtnCancel">Cancel</button>
            <button class="dr-btn dr-btn-draft" id="drBtnSaveDraft">Save Draft</button>
            <button class="dr-btn primary" id="drBtnSaveLive">Save &amp; Publish</button>
          </div>
        </div>
      `;

      document.body.appendChild(ov);

      // ── Category pills ────
      ov.querySelectorAll<HTMLButtonElement>("#drProductCatPills .dr-cat-pill").forEach((btn) => {
        btn.addEventListener("click", () => {
          flushSpecsBox();
          const newCat = btn.dataset["pcat"]!;
          const prevCat = current.productCategory;
          current.productCategory = newCat;
          ov.querySelector<HTMLInputElement>("#drProductCategory")!.value = newCat;
          ov.querySelectorAll<HTMLButtonElement>("#drProductCatPills .dr-cat-pill").forEach((b) => {
            b.classList.toggle("active", b.dataset["pcat"] === newCat);
          });
          if (newCat !== prevCat) {
            subCatsCopy.length = 0;
            const picker = ov.querySelector<HTMLElement>("#drSubCatPicker");
            if (picker) {
              picker.innerHTML = buildSubCatPicker(newCat);
              wireSubCatPicker();
            }
          }
          const specsHost = ov.querySelector<HTMLElement>("#drSpecsBox");
          if (specsHost) specsHost.innerHTML = buildSpecsHtml(newCat);
        });
      });

      // ── Sub-cat picker ────
      function wireSubCatPicker(): void {
        const picker = ov.querySelector<HTMLElement>("#drSubCatPicker");
        if (!picker) return;
        picker.querySelectorAll<HTMLButtonElement>("[data-sc-rm]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const idx = Number(btn.getAttribute("data-sc-rm"));
            subCatsCopy.splice(idx, 1);
            picker.innerHTML = buildSubCatPicker(String(current.productCategory));
            wireSubCatPicker();
          });
        });
        const sel = picker.querySelector<HTMLSelectElement>("#drSubCatSelect");
        sel?.addEventListener("change", () => {
          if (sel.value) addSubCat(sel.value);
        });

        const custom = picker.querySelector<HTMLInputElement>("#drSubCatCustom");
        const commit = (): void => {
          const v = custom?.value ?? "";
          if (!v.trim()) return;
          addSubCat(v);
          // addSubCat replaces the picker's DOM, so re-query rather than reuse
          // `custom` — and hand focus back so several can be typed in a row.
          ov.querySelector<HTMLInputElement>("#drSubCatCustom")?.focus();
        };
        picker.querySelector<HTMLButtonElement>("#drSubCatAdd")
          ?.addEventListener("click", commit);
        custom?.addEventListener("keydown", (e) => {
          if (e.key !== "Enter") return;
          // This sits inside the product dialog; a bare Enter would submit it.
          e.preventDefault();
          commit();
        });
      }
      wireSubCatPicker();

      // ── Price group ────
      ov.querySelectorAll<HTMLButtonElement>(".dr-pg-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
          ov.querySelectorAll<HTMLButtonElement>(".dr-pg-btn").forEach((b) => b.classList.remove("primary"));
          btn.classList.add("primary");
          ov.querySelector<HTMLInputElement>("#drPriceGroup")!.value = btn.dataset["pg"] ?? "";
        });
      });
      ov.querySelector<HTMLButtonElement>("#drPgClear")?.addEventListener("click", () => {
        ov.querySelector<HTMLInputElement>("#drPriceGroup")!.value = "";
        ov.querySelectorAll<HTMLButtonElement>(".dr-pg-btn").forEach((b) => b.classList.remove("primary"));
      });

      // ── Hero drop zone ────
      const heroDzSlot = ov.querySelector<HTMLElement>("#drHeroDzSlot");
      const heroInput = ov.querySelector<HTMLInputElement>("#drHero");
      if (heroDzSlot && heroInput) {
        const heroDz = makeDzZone(current.heroImage, (url) => { heroInput.value = url; });
        heroDz.el.style.cssText = "width:120px;height:80px;flex-shrink:0;";
        heroDzSlot.appendChild(heroDz.el);
        heroInput.addEventListener("input", () => {
          const url = heroInput.value.trim();
          const prev = heroDz.el.querySelector<HTMLElement>(".dz-preview");
          if (prev) prev.style.backgroundImage = url ? `url(${url})` : "";
          const hint = heroDz.el.querySelector<HTMLElement>(".dz-hint");
          const clear = heroDz.el.querySelector<HTMLElement>(".dz-clear");
          if (url) {
            heroDz.el.classList.add("has-image");
            if (hint) hint.style.display = "none";
            if (clear) clear.style.display = "";
          } else {
            heroDz.el.classList.remove("has-image");
            if (hint) hint.style.display = "";
            if (clear) clear.style.display = "none";
          }
        });
      }

      // ── PTO toggle ────
      const ptoCheck = ov.querySelector<HTMLInputElement>("#drPtoEnabled");
      ptoCheck?.addEventListener("change", () => {
        const det = ov.querySelector<HTMLElement>("#drPtoDetails");
        if (det) det.style.display = ptoCheck.checked ? "block" : "none";
      });

      // ── Colour grid ────
      const colourGrid = ov.querySelector<HTMLElement>("#drColourGrid")!;
      const colourLabel = ov.querySelector<HTMLElement>("#drColourLabel")!;
      let lastExtractedHex: string | null = null;

      function refreshColourGrid(): void {
        colourLabel.textContent = `Colour / Finish Swatches (${coloursCopy.length})`;
        renderColourGrid(colourGrid, coloursCopy,
          () => {
            drDialog({
              title: "Add Colour / Finish",
              fields: [
                { label: "Colour or Finish Name", placeholder: "e.g. Ivory, Brushed Brass", value: "" },
                { label: "Hex Code", placeholder: "#fefae8", value: lastExtractedHex ?? "", hexPreview: true },
                { label: "Availability", type: "select", options: ["Ex-Stock", "Made to Order"], value: "Ex-Stock" },
              ],
              confirmLabel: "Add",
            }).then((res) => {
              if (!res) return;
              const name = res.values[0] ?? "";
              let hex = res.values[1] || "#888888";
              const avail = (res.values[2] || "Ex-Stock") as Colour["availability"];
              if (!name) return;
              if (!/^#/.test(hex)) hex = "#" + hex;
              coloursCopy.push({ name, hex, availability: avail, frontalImage: "" });
              lastExtractedHex = null;
              refreshColourGrid();
            });
          },
          (idx) => {
            drDialog({
              title: "Remove Colour",
              body: `Remove "${coloursCopy[idx]?.name ?? ""}" from this product?`,
              confirmLabel: "Remove",
              danger: true,
            }).then((res) => {
              if (!res || !res.confirmed) return;
              coloursCopy.splice(idx, 1);
              refreshColourGrid();
            });
          });
      }
      refreshColourGrid();

      // ── Portfolio + close-up rows ────
      const portContainer = ov.querySelector<HTMLElement>("#drPortfolioList")!;
      const cuContainer = ov.querySelector<HTMLElement>("#drCloseUpList")!;

      function onRemovePort(idx: number): void { portfolioCopy.splice(idx, 1); renderPortfolio(); }
      function onRemoveCu(idx: number):   void { closeUpsCopy.splice(idx, 1);   renderCloseUps(); }

      function onExtractCu(url: string, _idx: number, rowEl: HTMLElement): void {
        const thumb = rowEl.querySelector<HTMLElement>(".dr-drop-zone");
        let spin: HTMLElement | null = null;
        if (thumb) {
          spin = document.createElement("span");
          spin.className = "dr-spin-mini";
          thumb.appendChild(spin);
        }
        void extractDominantColour(url).then((hex) => {
          if (spin && spin.parentNode) spin.remove();
          if (!hex) return;
          lastExtractedHex = hex;
          if (thumb) {
            const dot = document.createElement("div");
            dot.className = "dr-hex-dot";
            dot.style.background = hex;
            dot.title = `Sampled colour: ${hex}`;
            thumb.appendChild(dot);
          }
          toast(`Colour sampled: ${hex}`);
        });
      }

      function renderPortfolio(): void { renderImgRows(portContainer, portfolioCopy, "Add portfolio image", onRemovePort, null); }
      function renderCloseUps(): void { renderImgRows(cuContainer,    closeUpsCopy,  "Add close-up image", onRemoveCu,   onExtractCu); }
      renderPortfolio();
      renderCloseUps();

      // ── Close / Cancel ────
      function closeModal(): void { ov.remove(); }
      ov.querySelector<HTMLButtonElement>("#drMdClose")?.addEventListener("click", closeModal);
      ov.querySelector<HTMLButtonElement>("#drBtnCancel")?.addEventListener("click", closeModal);
      ov.addEventListener("click", (e) => { if (e.target === ov) closeModal(); });

      // ── Delete (modal) ────
      if (isEdit) {
        ov.querySelector<HTMLButtonElement>("#drBtnDelete")?.addEventListener("click", async () => {
          const res = await drDialog({
            title: "Delete Product",
            body: `Permanently delete "${current.name}"? This cannot be undone.`,
            confirmLabel: "Delete", danger: true,
          });
          if (!res || !res.confirmed) return;
          try {
            await ctx.api<OneResponse>(`/door-ranges/${encodeURIComponent(id!)}`, { method: "DELETE" });
            toast("Product deleted");
            closeModal();
            await refreshList();
          } catch {
            toast("Delete failed", true);
          }
        });
      }

      // ── Save ────
      async function doSave(status: "draft" | "live"): Promise<void> {
        flushImgRows(portContainer, portfolioCopy);
        flushImgRows(cuContainer,   closeUpsCopy);
        flushSpecsBox();
        const name = (ov.querySelector<HTMLInputElement>("#drName")?.value ?? "").trim();
        if (!name) {
          await drDialog({ title: "Validation", body: "Product name is required.", cancelLabel: false, confirmLabel: "OK" });
          return;
        }
        // Build payload — matches stage save shape exactly.
        const payload: Record<string, unknown> = {
          productCategory: ov.querySelector<HTMLInputElement>("#drProductCategory")?.value ?? "Doors",
          status,
          name,
          sku: (ov.querySelector<HTMLInputElement>("#drSku")?.value ?? "").trim(),
          subCategories: subCatsCopy.slice(),
          category: subCatsCopy[0] ?? "",
          description: (ov.querySelector<HTMLTextAreaElement>("#drDesc")?.value ?? "").trim(),
          priceGroup: ov.querySelector<HTMLInputElement>("#drPriceGroup")?.value ?? "",
          heroImage: (ov.querySelector<HTMLInputElement>("#drHero")?.value ?? "").trim(),
          heroImageColour: (ov.querySelector<HTMLInputElement>("#drHeroColour")?.value ?? "").trim(),
          portfolioImages: portfolioCopy.filter((e) => !!e.url),
          doorCloseUps: closeUpsCopy.filter((e) => !!e.url),
          colours: coloursCopy,
          specs: specsCopy,
          paintToOrder: {
            enabled: !!ov.querySelector<HTMLInputElement>("#drPtoEnabled")?.checked,
            description: (ov.querySelector<HTMLTextAreaElement>("#drPtoDesc")?.value ?? "").trim(),
          },
          styleTags: current.styleTags ?? [],
        };
        // Mirror common specs to top-level for back-compat with the legacy wizard reader.
        SPEC_BACKCOMPAT_KEYS.forEach((k) => {
          if (specsCopy[k] != null) payload[k] = specsCopy[k];
        });

        // Admin authoring on a supplier's behalf must name the supplier; the
        // server rejects the create otherwise, and validates the slug against
        // the supplier directory. In "self" scope we send nothing — the server
        // takes identity from the cookie and ignores any supplierId in the body.
        if (isAdminScope) {
          if (!selected) {
            await drDialog({
              title: "No supplier selected",
              body: "Choose which supplier's catalogue this range belongs to before saving.",
              cancelLabel: false, confirmLabel: "OK",
            });
            return;
          }
          payload["supplierId"]   = selected.slug;
          payload["supplierName"] = selected.name;
        }

        const draftBtn = ov.querySelector<HTMLButtonElement>("#drBtnSaveDraft");
        const liveBtn  = ov.querySelector<HTMLButtonElement>("#drBtnSaveLive");
        if (draftBtn) draftBtn.disabled = true;
        if (liveBtn)  { liveBtn.disabled = true; liveBtn.textContent = "Saving…"; }

        try {
          const body = JSON.stringify(payload);
          if (isEdit) {
            await ctx.api<OneResponse>(`/door-ranges/${encodeURIComponent(id!)}`, { method: "PUT", body });
          } else {
            await ctx.api<OneResponse>("/door-ranges", { method: "POST", body });
          }
          toast(status === "live" ? "Product published ✓" : "Draft saved ✓");
          closeModal();
          await refreshList();
        } catch (err) {
          if (draftBtn) draftBtn.disabled = false;
          if (liveBtn)  { liveBtn.disabled = false; liveBtn.textContent = "Save & Publish"; }
          await drDialog({
            title: "Save failed",
            body: err instanceof Error ? err.message : "Unknown error",
            cancelLabel: false,
            confirmLabel: "OK",
          });
        }
      }

      ov.querySelector<HTMLButtonElement>("#drBtnSaveDraft")?.addEventListener("click", () => void doSave("draft"));
      ov.querySelector<HTMLButtonElement>("#drBtnSaveLive")?.addEventListener("click",  () => void doSave("live"));
    }

    // ── Boot ─────────────────────────────────────────────────────────────
    renderListShell();
    if (isAdminScope) {
      // Nothing to list until a supplier is picked, so load the picker first.
      renderScopeCard();
      void loadSuppliers();
      void refreshList();
    } else {
      void refreshList();
    }

    return (): void => {
      ac.abort();
      // Remove any orphaned modals/dialogs on unmount.
      document.querySelectorAll(".dr-ov, .dr-dlg-ov").forEach((el) => el.remove());
    };
  },
  };

  return page;
}
